// Shared page states: loading, API errors (non-disclosing 404, 403, conflicts)
// and document titles.
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { ApiProblem } from "@/api/errors";
import { Button } from "@/ui/button";
import { Callout, EmptyState, Skeleton } from "@/ui/layout";

export function useTitle(title: string | undefined) {
  useEffect(() => {
    document.title = title ? `${title} · InteractAds` : "InteractAds";
  }, [title]);
}

export function PageLoading() {
  return (
    <div className="grid gap-4" aria-busy="true" aria-label="Cargando">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-40" />
    </div>
  );
}

/** Error view. 404 never reveals whether something exists in another workspace or scope. */
export function PageError({ error, orgId }: { error: unknown; orgId?: string }) {
  const problem = error instanceof ApiProblem ? error : null;
  if (problem?.status === 404) {
    return (
      <EmptyState
        title="No encontramos lo que buscas"
        description="Puede que no exista o que no tengas acceso a ello."
        action={
          orgId ? (
            <Button asChild>
              <Link to={`/o/${orgId}`}>Ir al Front Desk</Link>
            </Button>
          ) : null
        }
      />
    );
  }
  if (problem?.status === 403) {
    return (
      <Callout tone="warning" title="Sin permiso">
        {problem.message}
      </Callout>
    );
  }
  return (
    <Callout tone="danger" title="Algo salió mal">
      {problem?.message ?? "Ocurrió un problema inesperado. Recarga la página."}
    </Callout>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return <span className="text-muted">{children}</span>;
}

/** Named route params of the current match ("" when absent — such ids then simply 404). */
export function useRouteParams<K extends string>(...names: K[]): Record<K, string> {
  const params = useParams({ strict: false }) as unknown as Partial<Record<string, string>>;
  const out = {} as Record<K, string>;
  for (const name of names) out[name] = params[name] ?? "";
  return out;
}

/** Search params of the current location as plain values (validated where used). */
export function useSearchRecord(): Record<string, unknown> {
  return useSearch({ strict: false });
}
